// Insulation project math: wall/ceiling area → bags/batts by R-value and application.
// Batt: standard 15.5 sq ft per batt (24 in × 93 in, 16 in o.c.).
// Blown-in: bags per 1000 sq ft published on manufacturer bags (approx. values).
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface InsulationArea {
  length: number
  width: number
}

export type InsulationApplication = 'wall-batts' | 'ceiling-batts' | 'blown-in'
export type RValue = 'R-13' | 'R-15' | 'R-19' | 'R-21' | 'R-30' | 'R-38' | 'R-49'
export type StudSpacing = 16 | 24

// 'dimensions' = user enters one or more L × W sections.
// 'area' = user already knows the total area and types it directly.
export type InsulationInputMode = 'dimensions' | 'area'

export interface InsulationInputs {
  unit: UnitSystem
  inputMode: InsulationInputMode
  areas: InsulationArea[]
  totalArea: number   // sq ft (imperial) or m² (metric) — used in 'area' mode
  application: InsulationApplication
  rValue: RValue
  studSpacing: StudSpacing
  wastePct: number   // 5-15, default 10
  needVaporBarrier: boolean
}

export interface InsulationResult {
  totalArea: number        // sq ft before waste
  areaWithWaste: number    // sq ft after waste
  bags: number             // bags of batts or blown-in
  vaporBarrierRolls: number
  lines: AccessoryLine[]
}

// Batt coverage per bag (sq ft)
// 16 in o.c.: 93 in long × 15 in wide = 1162.5 sq in = 8.07 sq ft per batt — sold ~1 batt per bag at DIY stores
// But big-box stores sell batts in bags of 4–10 pieces; use sq ft per bag from pkg:
// R-13 (2×4): bag covers ~40 sq ft (10 batts × 14.5 in wide × 93 in)
// R-19 (2×6): bag covers ~40 sq ft (8 batts × 15 in wide × 93 in) ← 16" o.c.
// Simplification: use fixed sq ft per bag values calibrated to Owens Corning / Johns Manville SKUs
const BATT_COVERAGE: Record<RValue, Record<StudSpacing, number>> = {
  'R-13': { 16: 40, 24: 48 },
  'R-15': { 16: 40, 24: 48 },
  'R-19': { 16: 40, 24: 48 },
  'R-21': { 16: 38, 24: 46 },
  'R-30': { 16: 35, 24: 40 },  // ceiling batt — double layer often required
  'R-38': { 16: 32, 24: 38 },
  'R-49': { 16: 30, 24: 36 },
}

// Blown-in bags per 1000 sq ft at target R (from typical attic blown-in fiber glass product)
const BLOWN_IN_BAGS_PER_1000: Record<RValue, number> = {
  'R-13': 6,
  'R-15': 7,
  'R-19': 9,
  'R-21': 10,
  'R-30': 12,
  'R-38': 16,
  'R-49': 21,
}

const VAPOR_BARRIER_SQFT_PER_ROLL = 500

export const DEFAULT_INSULATION: InsulationInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  areas: [{ length: 12, width: 9 }],
  totalArea: 108,
  application: 'wall-batts',
  rValue: 'R-19',
  studSpacing: 16,
  wastePct: 10,
  needVaporBarrier: false,
}

export function computeInsulation(i: InsulationInputs, locale: Locale = 'en'): InsulationResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let totalArea = 0

  if (i.inputMode === 'area') {
    totalArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
  } else {
    for (const a of i.areas ?? []) {
      totalArea += rectArea(toFeet(a.length, i.unit), toFeet(a.width, i.unit))
    }
  }

  const areaWithWaste = applyWaste(totalArea, i.wastePct)

  let bags = 0
  if (totalArea > 0) {
    if (i.application === 'blown-in') {
      const bagsPerThousand = BLOWN_IN_BAGS_PER_1000[i.rValue] ?? 12
      bags = ceilTo((areaWithWaste / 1000) * bagsPerThousand, 1)
    } else {
      const coverage = BATT_COVERAGE[i.rValue]?.[i.studSpacing] ?? 40
      bags = ceilTo(areaWithWaste / coverage, 1)
    }
  }

  const vaporBarrierRolls = i.needVaporBarrier && totalArea > 0
    ? Math.max(1, ceilTo(totalArea / VAPOR_BARRIER_SQFT_PER_ROLL, 1))
    : 0

  const lines: AccessoryLine[] = []

  if (i.needVaporBarrier) {
    lines.push({
      item: 'poly vapor barrier sheeting 6 mil roll',
      label: tr('Vapor barrier', 'Pare-vapeur'),
      kind: 'consumable',
      qty: vaporBarrierRolls,
      unit: 'roll',
      note: tr(`6-mil poly · ${VAPOR_BARRIER_SQFT_PER_ROLL} sq ft rolls · install on warm side of insulation; check local code`, `Polyéthylène 6 mil · rouleaux de ${VAPOR_BARRIER_SQFT_PER_ROLL} pi² · à poser du côté chaud de l’isolant; vérifiez le code local`),
    })
  } else {
    lines.push({
      item: 'poly vapor barrier sheeting 6 mil roll',
      label: tr('Vapor barrier', 'Pare-vapeur'),
      kind: 'consumable',
      qty: 0,
      unit: 'roll',
      note: tr('Recommended in cold climates — check the option above to add', 'Recommandé en climat froid — cochez l’option ci-dessus pour l’ajouter'),
      optional: true,
    })
  }

  lines.push({
    item: 'staple gun heavy duty insulation stapler',
    label: tr('Staple gun + staples', 'Agrafeuse + agrafes'),
    kind: 'tool',
    note: i.application === 'blown-in'
      ? tr('Not needed for blown-in — use blocking netting instead', 'Inutile pour l’isolant soufflé — utilisez plutôt un filet de retenue')
      : tr('Heavy-duty stapler to fasten kraft-faced batts to studs', 'Agrafeuse robuste pour fixer les matelas à face kraft sur les montants'),
  })

  if (i.application === 'blown-in') {
    lines.push({
      item: 'insulation blower rental blown-in',
      label: tr('Insulation blower', 'Souffleuse à isolant'),
      kind: 'tool',
      note: tr('Many big-box stores lend or rent a blower when you buy a set number of bags — ask at the desk.', 'Plusieurs grandes surfaces prêtent ou louent une souffleuse à l’achat d’un nombre donné de sacs — informez-vous au comptoir.'),
    })
    lines.push({
      item: 'insulation netting blown-in attic',
      label: tr('Insulation netting', 'Filet de retenue à isolant'),
      kind: 'consumable',
      qty: Math.max(1, ceilTo(totalArea / 50, 1)),
      unit: 'roll',
      note: tr('50 sq ft rolls · staple to rafters/studs to hold blown-in against sloped ceilings or walls', 'Rouleaux de 50 pi² · à agrafer aux chevrons ou montants pour retenir l’isolant soufflé sur les plafonds en pente ou les murs'),
      optional: true,
    })
  }

  lines.push({
    item: 'utility knife box cutter blades',
    label: tr('Utility knife + blades', 'Couteau utilitaire + lames'),
    kind: 'tool',
    note: tr('Score and snap kraft-faced batts cleanly. Keep extra blades on hand.', 'Entaillez et cassez proprement les matelas à face kraft. Gardez des lames de rechange à portée de main.'),
  })

  lines.push({
    item: 'safety glasses dust mask n95 insulation',
    label: tr('Safety glasses + N95 mask', 'Lunettes de sécurité + masque N95'),
    kind: 'consumable',
    qty: 1,
    unit: 'set',
    note: tr('Fiberglass and mineral wool irritate skin, eyes and lungs — PPE is not optional', 'La fibre de verre et la laine minérale irritent la peau, les yeux et les poumons — l’équipement de protection n’est pas optionnel'),
  })

  lines.push({
    item: 'insulation work gloves long sleeve',
    label: tr('Work gloves + long sleeves', 'Gants de travail + manches longues'),
    kind: 'consumable',
    qty: 1,
    unit: 'pair',
    note: tr('Fiberglass fibres cause skin irritation — cover all skin when handling batts', 'Les fibres de verre causent des irritations cutanées — couvrez toute la peau en manipulant les matelas'),
  })

  return {
    totalArea: round(totalArea, 0),
    areaWithWaste: round(areaWithWaste, 0),
    bags,
    vaporBarrierRolls,
    lines,
  }
}
